import React from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";

// Render assistant message with markdown links [text](url) and **bold**
function ChatMessage({ content }) {
    // Split on markdown links [text](url)
    const parts = [];
    const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
    let last = 0, m;
    while ((m = linkRe.exec(content)) !== null) {
        if (m.index > last) parts.push({ type: "text", value: content.slice(last, m.index) });
        parts.push({ type: "link", text: m[1], href: m[2] });
        last = m.index + m[0].length;
    }
    if (last < content.length) parts.push({ type: "text", value: content.slice(last) });

    return (
        <span>
            {parts.map((p, i) => {
                if (p.type === "link") {
                    const isInternal = p.href.startsWith("/");
                    if (isInternal) {
                        return <Link key={i} to={p.href} className="text-primary underline font-medium hover:text-primary/80">{p.text}</Link>;
                    }
                    return <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" className="text-primary underline font-medium hover:text-primary/80">{p.text}</a>;
                }
                // Render text with **bold** support and newlines
                return <RenderText key={i} text={p.value} />;
            })}
        </span>
    );
}

function RenderText({ text }) {
    const boldRe = /\*\*([^*]+)\*\*/g;
    const lines = text.split("\n");
    return (
        <>
            {lines.map((line, li) => {
                const boldParts = [];
                let last = 0, m;
                while ((m = boldRe.exec(line)) !== null) {
                    if (m.index > last) boldParts.push(line.slice(last, m.index));
                    boldParts.push(<strong key={m.index}>{m[1]}</strong>);
                    last = m.index + m[0].length;
                }
                if (last < line.length) boldParts.push(line.slice(last));
                return (
                    <React.Fragment key={li}>
                        {li > 0 && <br />}
                        {boldParts}
                    </React.Fragment>
                );
            })}
        </>
    );
}

export default function ChatWidget() {
    const [open, setOpen] = React.useState(false);
    const [config, setConfig] = React.useState(null);
    const [messages, setMessages] = React.useState([]);
    const [input, setInput] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    // Persistent session id (expires after 1h of inactivity)
    const [sessionId] = React.useState(() => {
        try {
            const raw = localStorage.getItem("st_chat_session");
            if (raw) {
                const { id, expires } = JSON.parse(raw);
                if (id && expires && Date.now() < expires) return id;
            }
        } catch {}
        const id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        try { localStorage.setItem("st_chat_session", JSON.stringify({ id, expires: Date.now() + 60 * 60 * 1000 })); } catch {}
        return id;
    });

    // Extend session expiry on every message
    const touchSession = React.useCallback(() => {
        try { localStorage.setItem("st_chat_session", JSON.stringify({ id: sessionId, expires: Date.now() + 60 * 60 * 1000 })); } catch {}
    }, [sessionId]);
    const endRef = React.useRef(null);

    React.useEffect(() => {
        let mounted = true;
        api.get("/chat/config").then(r => {
            if (!mounted) return;
            setConfig(r.data);
            // Restore past history for this session when reloading the page
            api.get(`/chat/history?session_id=${encodeURIComponent(sessionId)}`)
                .then(hr => {
                    if (!mounted) return;
                    const hist = (hr.data?.messages || []).map(m => ({ role: m.role, content: m.content }));
                    if (hist.length > 0) {
                        setMessages(hist);
                    } else {
                        setMessages([{ role: "assistant", content: r.data?.greeting || "Salut!" }]);
                    }
                })
                .catch(() => {
                    setMessages([{ role: "assistant", content: r.data?.greeting || "Salut!" }]);
                });
        }).catch(() => {});
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || loading) return;
        setMessages(m => [...m, { role: "user", content: text }]);
        setInput("");
        setLoading(true);
        try {
            const res = await api.post("/chat", { session_id: sessionId, message: text });
            setMessages(m => [...m, { role: "assistant", content: res.data.response }]);
            touchSession();
        } catch {
            setMessages(m => [...m, { role: "assistant", content: "Ne pare rău, a apărut o eroare. Încearcă din nou." }]);
        } finally {
            setLoading(false);
        }
    };

    if (!config?.enabled) return null;

    const botName = config.title || "SecureBot";
    const botRole = config.subtitle || "Consultant AI";

    return (
        <>
            <button onClick={() => setOpen(!open)}
                    className="fixed bottom-6 right-6 z-30 bg-foreground text-background w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:bg-primary transition-colors"
                    data-testid="chat-toggle-btn" aria-label="Deschide chat">
                {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
            </button>
            {open && (
                <div className="fixed bottom-24 right-6 z-30 w-[360px] max-w-[calc(100vw-48px)] h-[500px] bg-card rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden" data-testid="chat-widget">
                    <div className="bg-foreground text-background p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center"><Bot className="h-5 w-5" /></div>
                        <div>
                            <div className="font-heading font-bold">{botName}</div>
                            <div className="text-xs text-background/70">{botRole}</div>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[80%] px-3 py-2 text-sm rounded-2xl ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                                    {m.role === "assistant" ? <ChatMessage content={m.content} /> : m.content}
                                </div>
                            </div>
                        ))}
                        {loading && <div className="text-xs text-muted-foreground">{botName} scrie...</div>}
                        <div ref={endRef} />
                    </div>
                    <form onSubmit={send} className="border-t border-border flex items-stretch bg-card">
                        <input value={input} onChange={(e) => setInput(e.target.value)} disabled={loading}
                               placeholder="Scrie întrebarea ta..."
                               className="flex-1 px-3 py-3 bg-transparent outline-none text-sm" data-testid="chat-input" />
                        <button type="submit" disabled={loading} className="px-4 bg-foreground text-background" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
                    </form>
                </div>
            )}
        </>
    );
}
